import React, { useEffect } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { LocalScannerDownloadBanner } from './local-scanner-download-banner';
import { LocalScannerDownloadButton } from './local-scanner-download-button';
import { ScanCartButton } from './scan-cart-button';
import { ScannerStaleness } from '@/hooks/offline-mode/use-local-scan-data';
import { ScannerType } from './scanner-types';
import { ScanPageContainer } from './scan-page-container';
import { ScanResultsSheet } from './scan-results-sheet';
import { useComponentFocus } from '@/hooks/use-component-focus';
import { useLocalScannerContext } from '@/contexts/local-scanner-context';
import { useMaybeOrderDrawerContext } from '@/contexts/order-drawer-context';

export const LocalScannerScreen: React.FC = () => {
  const {
    errorText,
    searchResults,
    offDeviceSearchResults,
    searchAndUpdateOrderFn,
    syncCartEntries,
    localScanData,
    updateCartWithMultiUpdates,
  } = useLocalScannerContext();

  const orderDrawerContext = useMaybeOrderDrawerContext();
  const [styles, theme] = useStyles();

  // Sync cart updates when closing order summary drawer on tablet (copied from existing implementation)
  useEffect(() => {
    if (orderDrawerContext && !orderDrawerContext.open) {
      syncCartEntries();
    }
  }, [orderDrawerContext, syncCartEntries]);

  // Sync cart updates when navigating back from order summary on mobile (copied from existing implementation)
  useComponentFocus({
    onFocus: syncCartEntries,
  });

  const hideCameraButtons = Boolean(
    (searchResults &&
      searchResults.orderGuideResults.length +
        searchResults.catalogResults.length >
        1) ||
      (offDeviceSearchResults && offDeviceSearchResults.length > 1),
  );

  return (
    <ScanPageContainer
      errorText={errorText}
      hideCameraButtons={hideCameraButtons}
      hideScanInput={!localScanData.hasData}
      navigationHeaderProps={(scannerType) => ({
        actions: [
          {
            component: (
              <LocalScannerDownloadButton
                iconProps={{
                  stroke:
                    scannerType === ScannerType.Camera
                      ? theme.sk.color.icon.ondark
                      : theme.sk.color.icon.primary,
                }}
                onPress={localScanData.startSync}
                staleness={localScanData.staleness ?? ScannerStaleness.None}
              />
            ),
          },
          {
            component: (
              <ScanCartButton
                fill={
                  scannerType === ScannerType.Camera
                    ? theme.sk.color.icon.ondark
                    : theme.sk.color.icon.primary
                }
                totalColor={
                  scannerType === ScannerType.Camera
                    ? theme.sk.color.text.ondark
                    : theme.sk.color.text.primary
                }
              />
            ),
          },
        ],
      })}
      onClearResults={updateCartWithMultiUpdates}
      scanInputProps={{ scanInputClearable: true }}
      searchByCode={searchAndUpdateOrderFn}
    >
      {(scannerType) => (
        <>
          {/* showing banner before initial check can prompt user to download unnecessarily */}
          {localScanData.hasTriedAutoSync || localScanData.isDownloading ? (
            <LocalScannerDownloadBanner
              cancelDownload={localScanData.cancelDownload}
              downloadProgress={localScanData.downloadProgress}
              hasData={localScanData.hasData}
              isDownloading={localScanData.isDownloading}
              startSync={localScanData.startSync}
            />
          ) : null}
          {localScanData.hasData && (
            <ScanResultsSheet
              containerStyle={[
                styles.base,
                scannerType === ScannerType.External
                  ? styles.externalContainer
                  : null,
              ]}
              scannerType={scannerType}
            />
          )}
        </>
      )}
    </ScanPageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  externalContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderTopLeftRadius: theme.sk.spacing.spaceXlarge,
    borderTopRightRadius: theme.sk.spacing.spaceXlarge,
    marginTop: theme.sk.spacing.spaceXsmall,
  },
}));
